import React from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Briefcase, Clock, CalendarDays, Settings as SettingsIcon, BarChart3 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { hasBookingPermission } from '../../utils/bookingAccess';
import BookingDashboard from './Dashboard';
import ServicesPage from './Services';
import AvailabilityPage from './Availability';
import BookingsPage from './Bookings';
import BookingDetailsPage from './BookingDetails';
import BookingSettingsPage from './Settings';
import BookingReportsPage from './Reports';

const TABS = [
  { path: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'ViewBookings' as const },
  { path: 'services', label: 'Services', icon: Briefcase, permission: 'ManageServices' as const },
  { path: 'availability', label: 'Availability', icon: Clock, permission: 'ManageAvailability' as const },
  { path: 'bookings', label: 'Bookings', icon: CalendarDays, permission: 'ViewBookings' as const },
  { path: 'reports', label: 'Reports', icon: BarChart3, permission: 'ViewReports' as const },
  { path: 'configuration', label: 'Configuration', icon: SettingsIcon, permission: 'ManageSettings' as const },
];

// Root router for the Booking & Scheduling module, mirrors the Settings module's tabbed layout.
export default function BookingModule() {
  const { token } = useAuth();
  const location = useLocation();

  const visibleTabs = TABS.filter((tab) => hasBookingPermission(token, tab.permission));
  const isTabActive = (path: string) => location.pathname === `/booking/${path}`;

  return (
    <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">

      {/* Header */}
      <header className="mt-5 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
            <CalendarDays className="text-strivre-burgundy" size={24} />
            Booking & Scheduling
          </h1>
          <p className="text-slate-500 text-sm mt-1 text-left">
            Manage services, availability, bookings, reports, and scheduling configuration.
          </p>
        </div>
      </header>

      {/* Navigation */}
      <div className="w-full lg:w-auto overflow-visible mb-6">
        <div className="flex flex-wrap gap-1 p-1 md:p-1.5 bg-strivre-softgrey rounded-2xl md:rounded-[1.25rem] border border-strivre-lightnavy shadow-inner">
          {visibleTabs.map((tab) => (
            <Link
              key={tab.path}
              to={`/booking/${tab.path}`}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs md:text-sm font-bold transition-all flex-1 lg:flex-none ${
                isTabActive(tab.path)
                  ? 'bg-strivre-burgundy text-white shadow-sm'
                  : 'text-slate-500 hover:text-strivre-burgundy'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </Link>
          ))}
        </div>
      </div>

      <Routes>
        <Route path="/" element={<Navigate to="dashboard" replace />} />
        {hasBookingPermission(token, 'ViewBookings') && (
          <Route path="dashboard" element={<BookingDashboard />} />
        )}
        {hasBookingPermission(token, 'ManageServices') && (
          <Route path="services" element={<ServicesPage />} />
        )}
        {hasBookingPermission(token, 'ManageAvailability') && (
          <Route path="availability" element={<AvailabilityPage />} />
        )}
        {hasBookingPermission(token, 'ViewBookings') && (
          <Route path="bookings" element={<BookingsPage />} />
        )}
        {hasBookingPermission(token, 'ViewBookings') && (
          <Route path="bookings/:id" element={<BookingDetailsPage />} />
        )}
        {hasBookingPermission(token, 'ViewReports') && (
          <Route path="reports" element={<BookingReportsPage />} />
        )}
        {hasBookingPermission(token, 'ManageSettings') && (
          <Route path="configuration" element={<BookingSettingsPage />} />
        )}
      </Routes>

    </div>
  );
}
